import { NonEmptyString } from "io-ts-types"
import { AType, FastCheckEnv } from "@silversea-ssc/functional-support"
import { brandFromPredicate } from "./brand-from-predicate"
import { brandString } from "./brand-string"
import * as F from "effect/Function"
import * as Schema from "effect/Schema"
import * as Arbitrary from "effect/Arbitrary"

const cruiseCodeRegex = /^([A-Z][A-Z0-9][0-9]{6}[0-9SC][0-9]{2}|WC[0-9]{4})$/
const cruiseCodeArbitrary = (fc: FastCheckEnv["fastCheck"]) => {
    const digit = fc.constantFrom(..."0123456789")
    const alpha = fc.constantFrom(..."ABCDEFGHIJKLMNOPQRSTUVWXYZ")
    const ship = fc.tuple(alpha, fc.oneof(alpha, digit)).map((x: string[]) => x.join(""))
    const time = fc.stringOf(digit, { minLength: 6, maxLength: 6 })
    const typeAndDuration = fc
        .tuple(fc.oneof(fc.constant("S"), fc.constant("C"), digit), fc.stringOf(digit, { minLength: 2, maxLength: 2 }))
        .map((x: string[]) => x.join(""))
    return fc.oneof(
        fc.stringOf(digit, { minLength: 4, maxLength: 4 }).map((x: string) => `WC${x}`),
        fc.tuple(ship, time, typeAndDuration).map((x: string[]) => x.join("")),
    )
}
const cruiseCodeLazyArbitrary: Arbitrary.LazyArbitrary<CruiseCode> = (fc) => {
    const digit = fc.constantFrom(..."0123456789")
    const alpha = fc.constantFrom(..."ABCDEFGHIJKLMNOPQRSTUVWXYZ")
    const ship = fc.tuple(alpha, fc.oneof(alpha, digit)).map((x: string[]) => x.join(""))
    const time = fc.string({ unit: digit, minLength: 6, maxLength: 6 })
    const typeAndDuration = fc
        .tuple(
            fc.oneof(fc.constant("S"), fc.constant("C"), digit),
            fc.string({ unit: digit, minLength: 2, maxLength: 2 }),
        )
        .map((x: string[]) => x.join(""))
    return fc
        .oneof(
            fc.string({ unit: digit, minLength: 4, maxLength: 4 }).map((x: string) => `WC${x}`),
            fc.tuple(ship, time, typeAndDuration).map((x: string[]) => x.join("")),
        )
        .map((x) => x as CruiseCode)
}

const type = brandFromPredicate(NonEmptyString, (x) => cruiseCodeRegex.test(x), "CruiseCode")

export const CruiseCode = /* @__PURE__ */ brandString(type, cruiseCodeArbitrary)
export type CruiseCode = AType<typeof CruiseCode>
const filterCruiseCodeForBetterTreeShaking = (x: string) => CruiseCode.strictType.is(x)

export const CruiseCodeV2 = /* @__PURE__ */ F.pipe(
    /* @__PURE__ */ Schema.String,
    /* @__PURE__ */ Schema.filter(filterCruiseCodeForBetterTreeShaking, {
        identifier: "CruiseCode",
        arbitrary: () => cruiseCodeLazyArbitrary,
    }),
)
